import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { groupBy, isEmpty } from 'ramda';
import { useQuery } from '@apollo/react-hooks';
import { GET_CANDIDATES_CHART } from '../apollo/queries';
import { Page, Card, AppProvider, Select, Frame, Stack, TextField, DataTable, EmptyState } from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import Loader from '../components/SkeletonLoader';
import { Bar } from 'react-chartjs-2';
import randomColor from 'randomcolor';

const dateOptions = [
  { label: 'All', value: 'all' },
  { label: 'Today', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last year', value: '365' },
];

const CandidatesSourcingReport = ({ history, shopifyToken }) => {
  const { data: candidatesData = {}, loading: loadingCandidates } = useQuery(GET_CANDIDATES_CHART, {
    variables: { shopifyToken },
  });
  const loading = loadingCandidates;

  const { candidates = [] } = { ...candidatesData };
  let jobOptions = candidates.map((el) => {
    const { job = {}, jobTitle: label = '' } = { ...el };
    const { id: value } = { ...job };
    return { label, value };
  });
  jobOptions = [
    { label: 'All jobs', value: '' },
    ...jobOptions.filter((el, index) => {
      const { value } = { ...el };
      const idx = jobOptions.findIndex((i) => i.value === value);
      return idx === index;
    }),
  ];

  let deviceOptions = candidates.map((el) => {
    const { visitorData = {} } = { ...el };
    const { device = '' } = { ...visitorData };
    const value = !!device ? device : 'other';
    return { label: value.charAt(0).toUpperCase() + value.slice(1), value };
  });
  deviceOptions = [
    { label: 'All devices', value: '' },
    ...deviceOptions.filter((el, index) => {
      const { value } = { ...el };
      const idx = deviceOptions.findIndex((i) => i.value === value);
      return idx === index;
    }),
  ];

  const [selectedJob, setSelectedJob] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [candidatesTable, setCandidatesTable] = useState([]);
  const [candidatesTotals, setCandidatesTotals] = useState([]);
  const [overTimeData, setOverTimeData] = useState();

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [dateRange, setDateRange] = useState('all');
  const handleDateRangeChange = useCallback((newValue) => {
    setDateRange(newValue);
    if (newValue === 'all') {
      setStartDate();
    } else {
      setStartDate(moment(new Date()).subtract(Number(newValue), 'days'));
    }
  }, []);

  const filterCandidates = () => {
    let selectedCandidates = !!selectedJob
      ? candidates.filter((el) => {
          const { job = {} } = { ...el };
          const { id = '' } = { ...job };
          return id === selectedJob;
        })
      : candidates;

    selectedCandidates = !!selectedDevice
      ? selectedCandidates.filter((el) => {
          const { visitorData = {} } = { ...el };
          const { device = '' } = { ...visitorData };
          return device === selectedDevice || (selectedDevice === 'other' && !device);
        })
      : selectedCandidates;

    selectedCandidates = selectedCandidates.filter(({ createdAt }) => {
      return moment(createdAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date()));
    });
    return selectedCandidates;
  };

  const buildOverTimeData = () => {
    const selectedCandidates = filterCandidates();

    let selectedDates = Array.from(
      new Set(selectedCandidates.map(({ createdAt }) => moment(createdAt).format('MMMM DD YYYY')))
    ).sort((a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'));

    const selectedSources = Array.from(
      new Set(
        selectedCandidates.map(({ source = 'Other' }) => {
          return !!source ? source : 'Other';
        })
      )
    );

    const rgbColors = selectedSources.map(() => randomColor({ format: 'rgbArray' }));

    const datasets = selectedSources.map((el, ind) => {
      const borderColor = `rgba(${rgbColors[ind][0]}, ${rgbColors[ind][1]}, ${rgbColors[ind][2]}, 0.2)`;
      const backgroundColor = `rgba(${rgbColors[ind][0]}, ${rgbColors[ind][1]}, ${rgbColors[ind][2]}, 0.6)`;
      return {
        data: selectedDates.map((date) => {
          return selectedCandidates.filter(({ createdAt, source = 'Other' }) => {
            return moment(createdAt).format('YYYYMMDD') === moment(date).format('YYYYMMDD') && (source || 'Other') === el;
          }).length;
        }),
        label: el || 'Other',
        backgroundColor,
        borderColor,
        borderWidth: 1,
      };
    });

    setOverTimeData({
      labels: selectedDates,
      datasets,
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  useEffect(() => {
    buildOverTimeData();
    buildCandidatesTable();
  }, [selectedJob, selectedDevice, candidates, startDate, endDate]);

  const buildCandidatesTable = () => {
    const selectedCandidates = filterCandidates();
    const groupedCandidates = groupBy(({ source = 'Other' }) => source || 'Other', selectedCandidates);

    const rows = Object.keys(groupedCandidates).map((sourceKey) => {
      const sourcedCandidates = groupBy(({ status }) => status, groupedCandidates[sourceKey]);
      return [
        sourceKey,
        (sourcedCandidates['new_candidate'] || []).length,
        (sourcedCandidates['active'] || []).length,
        (sourcedCandidates['hired'] || []).length,
        (sourcedCandidates['inactive'] || []).length,
        (sourcedCandidates['new_candidate'] || []).length +
          (sourcedCandidates['active'] || []).length +
          (sourcedCandidates['hired'] || []).length +
          (sourcedCandidates['inactive'] || []).length,
      ];
    });
    const statusCandidates = groupBy(({ status }) => status, selectedCandidates);
    setCandidatesTable(rows);
    setCandidatesTotals([
      '',
      (statusCandidates['new_candidate'] || []).length,
      (statusCandidates['active'] || []).length,
      (statusCandidates['hired'] || []).length,
      (statusCandidates['inactive'] || []).length,
      (statusCandidates['new_candidate'] || []).length +
        (statusCandidates['active'] || []).length +
        (statusCandidates['hired'] || []).length +
        (statusCandidates['inactive'] || []).length,
    ]);
  };

  if (!loading) {
    return (
      <AppProvider>
        <Frame>
          <TitleBar title='Candidate sourcing' />
          <Page breadcrumbs={[{ content: 'Analytics', onAction: () => { history.push('/analytics'); } }]} title='Candidate sourcing'>
            <Stack vertical>
              <Stack distribution='fill'>
                <Select label={'Date range'} options={dateOptions} onChange={handleDateRangeChange} value={dateRange} />
                <Select label='Job title' options={jobOptions} onChange={setSelectedJob} value={selectedJob} />
                <Select label='Device' options={deviceOptions} onChange={setSelectedDevice} value={selectedDevice} />
                <Stack.Item></Stack.Item>
              </Stack>
              <Card>
                <Card.Section title={'New candidates over time'}>
                  {(overTimeData || {}).isEmpty ? (
                    <EmptyState heading='No data found'>
                      <p>{`There were no new candidates in this date range.`}</p>
                    </EmptyState>
                  ) : (
                    <Bar
                      data={overTimeData}
                      options={{
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                              stacked: true,
                            },
                          ],
                          xAxes: [
                            {
                              stacked: true,
                              type: 'time',
                              distribution: 'series',
                              time: {
                                unit: 'day',
                              },
                            },
                          ],
                        },
                      }}
                    />
                  )}
                </Card.Section>
              </Card>
              <span className={'charts-datatable'}>
                <Card>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={['Source', 'New', 'Active', 'Hired', 'Disqualified', 'Total']}
                    rows={candidatesTable}
                    totals={candidatesTotals}
                    totalsName={{ plural: 'Summary', singular: 'Summary' }}
                    footerContent={`Showing ${candidatesTotals[5]} of ${candidates.length} results`}
                  />
                </Card>
              </span>
            </Stack>
            <br />
          </Page>
        </Frame>
      </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <Loader />
      </AppProvider>
    );
  }
};

export default CandidatesSourcingReport;
